import React, { Suspense, lazy, useRef, useState, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import {
  logoWhiteImg,
  cancelWhiteIcon,
  menuBarImg,
  arrowDownWhite,
  arrowUpYellowIcon,
  loginIcon,
  signupIcon,
  yellowAvatar,
} from "../../images";
import { Link } from "react-router-dom";
import Dropdown from "./dropdown";
import styles from "./navbar.module.css";
import Button from "../button";
import Banner from "../banner";
import { CALENDLY_URL } from "../../utils/constants";
import {
  logout,
  showModalFunc,
  showSignUpModal,
  showMentorSignUpModal,
  fetchCourseGroups,
} from "../../redux/action";

const PopupModal = lazy(() =>
  import("react-calendly").then((module) => ({
    default: module.PopupModal,
  })),
);

const AuthBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showSignUpDropdown, setShowSignUpDropdown] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const signupDropdownRef = useRef(null);
  const loginDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  const { isAuthenticated, user } = useSelector((state) => state);
  const experiences = useSelector((state) => state.experiences);

  const handleLogOut = () => {
    Cookies.remove("token");
    dispatch(logout());
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(event.target)) {
        setShowLoginDropdown(false);
      }
      if (signupDropdownRef.current && !signupDropdownRef.current.contains(event.target)) {
        setShowSignUpDropdown(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    dispatch(fetchCourseGroups());
  }, [dispatch]);

  return (
    <div className={styles["auth_bar"]}>
      <div className={styles["auth"]}>
        {isAuthenticated ? (
          <div 
            className={styles["user_dropdown_wrapper"]}
            ref={userDropdownRef}
          >
            <div 
              className={styles["user_trigger"]}
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              <div className={styles["avatar_container"]}>
                <img src={yellowAvatar} alt="User Avatar" />
              </div>
              <span>{user?.firstName || user?.username || 'User'}</span>
              <svg 
                width="10" 
                height="6" 
                viewBox="0 0 10 6" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ 
                  transition: 'transform 0.2s ease',
                  transform: showUserDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path 
                  d="M1 1L5 5L9 1" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {showUserDropdown && (
              <div className={styles["user_dropdown"]}>
                <div 
                  className={styles["user_dropdown_item"]}
                  onClick={() => {
                    navigate('/dashboard');
                    setShowUserDropdown(false);
                  }}
                >
                  Student Dashboard
                </div>
                {experiences?.mentor?.active && (
                  <div 
                    className={styles["user_dropdown_item"]}
                    onClick={() => {
                      navigate('/mentor-profile/me');
                      setShowUserDropdown(false);
                    }}
                  >
                    Mentor Dashboard
                  </div>
                )}
                <div 
                  className={styles["user_dropdown_item"]}
                  onClick={() => {
                    handleLogOut();
                    setShowUserDropdown(false);
                  }}
                >
                  Log Out
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              alt="login"
              className={styles["signup_dropdown_wrapper"]}
              ref={loginDropdownRef}
            >
              <div
                className={styles["signup_trigger"]}
                onClick={() => setShowLoginDropdown(!showLoginDropdown)}
              >
                <img src={loginIcon} alt="loginIcon" />
                <span>Log In</span>
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transition: "transform 0.2s ease",
                    transform: showLoginDropdown ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {showLoginDropdown && (
                <div className={styles["signup_dropdown"]}>
                  <div
                    className={styles["signup_dropdown_item"]}
                    onClick={() => {
                      dispatch(showModalFunc("student"));
                      setShowLoginDropdown(false);
                    }}
                  >
                    As a Student
                  </div>
                  <div
                    className={styles["signup_dropdown_item"]}
                    onClick={() => {
                      dispatch(showModalFunc("mentor"));
                      setShowLoginDropdown(false);
                    }}
                  >
                    As a Mentor
                  </div>
                </div>
              )}
            </div>
            <div 
              alt="signup" 
              className={styles["signup_dropdown_wrapper"]}
              ref={signupDropdownRef}
            >
              <div 
                className={styles["signup_trigger"]}
                onClick={() => setShowSignUpDropdown(!showSignUpDropdown)}
              >
                <img src={signupIcon} alt="signupIcon" />
                <span>Sign Up</span>
                <svg 
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    transition: 'transform 0.2s ease',
                    transform: showSignUpDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <path 
                    d="M1 1L5 5L9 1" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {showSignUpDropdown && (
                <div className={styles["signup_dropdown"]}>
                  <div 
                    className={styles["signup_dropdown_item"]}
                    onClick={() => {
                      dispatch(showSignUpModal());
                      setShowSignUpDropdown(false);
                    }}
                  >
                    As a Student
                  </div>
                  <div 
                    className={styles["signup_dropdown_item"]}
                    onClick={() => {
                      dispatch(showMentorSignUpModal());
                      setShowSignUpDropdown(false);
                    }}
                  >
                    As a Mentor
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Navbar = ({ showBanner, setShowBanner, bannerData, setHeight, hideAuthBar, mentorPage }) => {
  const [width, setWidth] = useState(window.innerWidth);
  const [menu, setMenu] = useState(window.innerWidth < 992 ? true : false);
    const dispatch = useDispatch();
  const handleMenu = () => setMenu(window.innerWidth < 992 ? !menu : false);
  const [isOpen, setIsOpen] = useState(false);
  const rootElement = document.getElementById("root");

  const [dropdown, setDropdown] = useState(false);

  const handleOpenModal = () => {
    setIsOpen(true);
    width < 992 && handleMenu();
  };
  const handleCloseModal = () => {
    setIsOpen(false);
  };

  //choose the screen size
  const handleResize = () => {
    setWidth(window.innerWidth);
    if (window.innerWidth < 992) {
      setMenu(true);
    } else {
      setMenu(false);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // scrolling

  const handleMenuScroll = () => {
    handleMenu();
  };

  const navbarRef = useRef(null);
  useLayoutEffect(() => {
    // Access the height of the component using ref
    if (navbarRef.current) {
      const navbarHeight = navbarRef.current.clientHeight;
      setHeight(navbarHeight);
    }
  }, [navbarRef?.current?.clientHeight, showBanner, width, menu]);

  const courses = useSelector((state) => state.courses);
  const courseGroups = useSelector((state) => state.courseGroups);

  return (
    <section className={styles["main"]} ref={navbarRef}>
      {showBanner && (
        <Banner
          showBanner={showBanner}
          setShowBanner={setShowBanner}
          {...bannerData}
        />
      )}
      {!hideAuthBar && (width >= 992 || menu) && <AuthBar />}
      <div className={styles["header"]}>
        <div className={`${styles["header_body"]} ${mentorPage ? styles["header_body_mentor"] : ""}`}>
          <div className={`${styles["logo"]} ${mentorPage ? styles["logo_mentor"] : ""}`}>
            <Link to="/">
              <img
                src={logoWhiteImg}
                alt="nav-logo"
                onClick={() => window.scrollTo(0, 0)}
              />
            </Link>
          </div>
          <div
            className={!menu ? styles["back_filter"] : ""}
            onClick={handleMenu}
          />
          {menu && (
            <button
              title="menu icon"
              className={`${styles["btn"]} ${styles["menu_icon"]} ${styles["cursor_pointer"]}`}
              onClick={handleMenu}
            >
              <img src={menuBarImg} alt="menu icon" />
            </button>
          )}
          {!menu && (
            <div
              className={`${styles["nav"]} ${dropdown && styles["long_nav"]}`}
            >
              <div className={`${styles["menu_items"]} ${mentorPage ? styles["menu_items_mentor"] : ""}`}>
                {!(width >= 992 || menu) && <AuthBar />}
                {!(width < 992 && dropdown) && (
                  <section className={mentorPage ? styles["mentor_section"] : ""}>
                    <div className={styles["mobile_menu"]}>
                      <img
                        src={cancelWhiteIcon}
                        alt=""
                        onClick={handleMenu}
                        className={styles["arrow_icon"]}
                      />
                    </div>
                    {mentorPage ? (
                      <>
                        <div
                          className={styles["button_parent"]}
                          onMouseOver={(e) => {
                            setDropdown(true);
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            setDropdown(true);
                            e.stopPropagation();
                          }}
                        >
                          <button
                            className={`${styles["nav_title"]} ${styles["nav_title_mentor"]} ${styles["btn"]} ${
                              dropdown ? styles["active"] : ""
                            }`}
                          >
                            <span>For Students</span>
                            <img
                              src={dropdown ? arrowUpYellowIcon : arrowDownWhite}
                              alt=""
                            />
                          </button>
                        </div>
                        <button
                          className={`${styles["nav_title"]} ${styles["nav_title_mentor"]} ${styles["btn"]}`}
                        >
                          <span>For Companies</span>
                          <img src={arrowDownWhite} alt="" />
                        </button>
                        <button
                          className={`${styles["nav_title"]} ${styles["nav_title_mentor"]} ${styles["btn"]}`}
                        >
                          <span>Resources</span>
                          <img src={arrowDownWhite} alt="" />
                        </button>
                        <Link
                          to={"/about"}
                          className={`${styles["nav_title"]} ${styles["nav_title_mentor"]}`}
                        >
                          About Us
                        </Link>
                      </>
                    ) : (
                      <>
                        <div
                          className={styles["button_parent"]}
                          onMouseOver={(e) => {
                            setDropdown(true);
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            setDropdown(true);
                            e.stopPropagation();
                          }}
                        >
                          <button
                            className={`${styles["nav_title"]} ${styles["btn"]}  ${
                              dropdown ? styles["active"] : ""
                            }`}
                          >
                            <span>For Students</span>
                            <img
                              src={dropdown ? arrowUpYellowIcon : arrowDownWhite}
                              alt=""
                            />
                          </button>
                        </div>

                        <Link
                          to={"/about"}
                          className={`${styles["menu_items_border"]} ${styles["nav_title"]} ${styles["shadow_border"]}`}
                        >
                          About Us
                        </Link>
                        <Link
                          to={"/testimonies"}
                          className={`${styles["menu_items_border"]} ${styles["nav_title"]} ${styles["shadow_border"]}`}
                        >
                          Testimonies
                        </Link>
                        <Link
                          to={"/tech-experience-course"}
                          className={`${styles["menu_items_border"]} ${styles["nav_title"]} ${styles["shadow_border"]}`}
                        >
                          BA Work Experience 
                        </Link>
                      </>
                    )}
                  </section>
                )}
                {width < 992 && (
                  <Dropdown
                    show={dropdown}
                    menu={courseGroups}
                    handleMenu={handleMenuScroll}
                    setDropdown={setDropdown}
                    topOffset={hideAuthBar ? 78 : 118}
                  />
                )}
              </div>

              <div className={`${styles["buttons_block"]} ${mentorPage ? styles["buttons_block_mentor"] : ""}`}>
                {mentorPage && (
                  <button
                    className={`${styles["nav_title"]} ${styles["nav_title_mentor"]} ${styles["btn"]}`}
                    onClick={() => dispatch(showModalFunc("student"))}
                  >
                    Sign in
                  </button>
                )}
                <Button variant="secondary" onClick={mentorPage ? handleOpenModal : handleOpenModal} className={mentorPage ? "navbar-mentor-btn" : ""}>
                  Get Started
                </Button>
              </div>
              {
                // not mobile
                !(width < 992) && (
                  <Dropdown
                    show={dropdown}
                    menu={courseGroups}
                    handleMenu={handleMenuScroll}
                    setDropdown={setDropdown}
                    topOffset={hideAuthBar ? 78 : 118}
                  />
                )
              }
            </div>
          )}
        </div>
      </div>
      {isOpen && (
        <Suspense fallback={null}>
          <PopupModal
            url={CALENDLY_URL}
            onModalClose={handleCloseModal}
            open={isOpen}
            rootElement={rootElement}
          />
        </Suspense>
      )}
    </section>
  );
};

export default Navbar;
